import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/cn'
import { formatRelativeTime } from '@/shared/lib/formatRelativeTime'

import { useMarkSeveralAsReadMutation } from '../hooks/useNotifications'
import { groupNotifications, notificationSubject } from '../lib/groupNotifications'
import { notificationDecoration } from '../lib/notificationDecoration'
import { notificationGroupKey } from '../lib/notificationGroupKeys'
import type { NotificationResponse } from '../types/notification.types'

import { NotificationItem } from './NotificationItem'

interface Props {
  notifications: NotificationResponse[]
  /** Novedades: los avisos del mismo tipo se juntan en una fila. */
  grouped: boolean
  onSelect: (notification: NotificationResponse) => void
  className?: string
}

/** Lista de avisos de la campana o de la página; en Novedades, agrupada por tipo. */
export function NotificationList({ notifications, grouped, onSelect, className }: Props) {
  const entries = grouped
    ? groupNotifications(notifications)
    : notifications.map((n) => ({ kind: 'single' as const, notification: n }))
  return (
    <ul className={cn('divide-y divide-foreground/10', className)}>
      {entries.map((e) =>
        e.kind === 'single' ? (
          <NotificationItem
            key={e.notification.id}
            notification={e.notification}
            onSelect={onSelect}
          />
        ) : (
          <NotificationGroupRow
            key={`group-${e.type}`}
            type={e.type}
            items={e.items}
            onSelect={onSelect}
          />
        ),
      )}
    </ul>
  )
}

/**
 * Fila de un grupo: «8 mesas nuevas cerca de ti · Catan, Wingspan, Azul y 5 más». Al
 * desplegarla se ven todos los avisos y los que estaban sin leer quedan leídos de una vez.
 */
function NotificationGroupRow({
  type,
  items,
  onSelect,
}: {
  type: string
  items: NotificationResponse[]
  onSelect: (notification: NotificationResponse) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const markSeveral = useMarkSeveralAsReadMutation()
  const deco = notificationDecoration(type)
  const unreadIds = items.filter((n) => n.readAt === null).map((n) => n.id)
  const subjects = [...new Set(items.map(notificationSubject).filter((s): s is string => !!s))]
  const shown = subjects.slice(0, 3)
  const names =
    subjects.length > shown.length
      ? t('notifications.group.namesMore', {
          names: shown.join(', '),
          count: subjects.length - shown.length,
        })
      : shown.join(', ')
  const panelId = `notification-group-${type}`

  function toggle() {
    if (!open && unreadIds.length > 0) markSeveral.mutate(unreadIds)
    setOpen((v) => !v)
  }

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'relative flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors',
          unreadIds.length > 0
            ? 'bg-yellow/20 hover:bg-yellow/40'
            : 'opacity-80 hover:bg-yellow/20',
        )}
      >
        {unreadIds.length > 0 && (
          <span className="absolute inset-y-0 left-0 w-1 bg-red" aria-hidden="true" />
        )}
        <span
          aria-hidden="true"
          className={cn(
            'chip flex size-9 shrink-0 items-center justify-center rounded-md',
            deco.bgClass,
            deco.textClass,
          )}
        >
          {deco.icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold leading-tight">
            {t(notificationGroupKey(type), { count: items.length })}
          </p>
          {names && (
            <p className="mt-0.5 line-clamp-2 break-words font-brutal text-[10px] text-muted-foreground">
              {names}
            </p>
          )}
          <p className="mt-1 font-brutal text-[9px] uppercase tracking-widest text-muted-foreground">
            {formatRelativeTime(items[0]!.createdAt, i18n.language)}
          </p>
        </div>
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          aria-hidden="true"
          className={cn('mt-1 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <ul
          id={panelId}
          className="divide-y divide-foreground/10 border-t border-foreground/10 bg-background-alt/40 pl-3"
        >
          {items.map((n) => (
            <NotificationItem key={n.id} notification={n} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </li>
  )
}
